import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { adminAPI, usersAPI } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  ArrowLeft, Mail, Phone, Building, Calendar, Package, ShoppingBag, 
  MessageSquare, Edit, Tag as TagIcon, ExternalLink, CreditCard,
  MapPin, FileText, Receipt, Camera, Trash2, Upload, AlertTriangle
} from 'lucide-react';
import { formatPrice, formatDate } from '../../lib/utils';

const BACKEND_URL = '';

export default function AdminClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showTagDialog, setShowTagDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [editForm, setEditForm] = useState({});
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => {
    loadClient();
  }, [id]);

  const loadClient = async () => {
    try {
      const response = await adminAPI.getClientDetail(id);
      setClient(response.data);
      setEditForm({
        full_name: response.data.full_name,
        email: response.data.email,
        company_name: response.data.company_name || '',
        phone: response.data.phone || '',
        vat_number: response.data.vat_number || '',
        billing_address: response.data.billing_address || '',
        billing_city: response.data.billing_city || '',
        billing_postal_code: response.data.billing_postal_code || '',
        billing_country: response.data.billing_country || '',
      });
    } catch (error) {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = () => {
    avatarInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG and PNG files are allowed');
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    setUploadingAvatar(true);
    try {
      const response = await usersAPI.uploadAvatar(id, file);
      toast.success('Avatar uploaded successfully');
      loadClient(); // Reload to get new avatar URL
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
      // Reset input
      if (avatarInputRef.current) {
        avatarInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Remove this avatar?')) return;
    
    try {
      await usersAPI.deleteAvatar(id);
      toast.success('Avatar removed');
      loadClient();
    } catch (error) {
      toast.error('Failed to remove avatar');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await adminAPI.createClientNote(id, newNote);
      toast.success('Note added');
      setNewNote('');
      loadClient();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;

    try {
      await adminAPI.deleteClientNote(id, noteId);
      toast.success('Note deleted');
      loadClient();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleUpdateClient = async () => {
    try {
      await adminAPI.updateUser(id, editForm);
      toast.success('Client updated');
      setShowEditDialog(false);
      loadClient();
    } catch (error) {
      toast.error('Failed to update client');
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim()) return;

    try {
      await adminAPI.addUserTag(id, newTag);
      toast.success('Tag added');
      setNewTag('');
      setShowTagDialog(false);
      loadClient();
    } catch (error) {
      toast.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (tag) => {
    try {
      await adminAPI.removeUserTag(id, tag);
      toast.success('Tag removed');
      loadClient();
    } catch (error) {
      toast.error('Failed to remove tag');
    }
  };

  const handleDeleteClient = async () => {
    setDeleting(true);
    try {
      await adminAPI.deleteUser(id);
      toast.success('Client deleted successfully');
      navigate('/admin/clients');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to delete client';
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const canDeleteClient = () => {
    // Check if client has active services
    return (client?.active_services_count || 0) === 0;
  };

  const handleManageClientPayment = async () => {
    try {
      const returnUrl = window.location.href;
      const response = await adminAPI.manageClientPayment(id, returnUrl);
      
      if (response.data.url) {
        window.open(response.data.url, '_blank');
        toast.success('Opening payment portal for client');
      } else if (response.data.requires_configuration) {
        toast.error(response.data.message || 'Payment management requires Stripe API configuration');
      }
    } catch (error) {
      toast.error('Failed to open payment management');
    }
  };

  const getAvatarUrl = () => {
    if (client?.avatar_url) {
      return `${BACKEND_URL}${client.avatar_url}`;
    }
    return null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        </div>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout>
        <Card>
          <CardContent className="pt-6 text-center py-12">
            <h3 className="text-lg font-semibold mb-2">Client not found</h3>
            <Link to="/admin/clients">
              <Button>Back to Clients</Button>
            </Link>
          </CardContent>
        </Card>
      </Layout>
    );
  }

  return (
    <TooltipProvider>
      <Layout>
        <div className="space-y-6">
          {/* Header */}
          <div>
            <Link to="/admin/clients">
              <Button variant="ghost" className="mb-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Clients
              </Button>
            </Link>

            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                {/* Clickable Avatar */}
                <div className="relative group">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleAvatarClick}
                        disabled={uploadingAvatar}
                        className="relative w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-primary hover:ring-offset-2 transition-all disabled:opacity-50"
                      >
                        {getAvatarUrl() ? (
                          <img 
                            src={getAvatarUrl()} 
                            alt={client.full_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-primary font-bold text-2xl">
                            {client.full_name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        )}
                        
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          {uploadingAvatar ? (
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                          ) : (
                            <Camera className="w-6 h-6 text-white" />
                          )}
                        </div>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Click to upload avatar (JPG/PNG, max 2MB)</p>
                    </TooltipContent>
                  </Tooltip>
                  
                  {/* Delete avatar button */}
                  {client.avatar_url && (
                    <button
                      onClick={handleDeleteAvatar}
                      className="absolute -bottom-1 -right-1 p-1.5 bg-destructive text-white rounded-full hover:bg-destructive/90 transition-colors"
                      title="Remove avatar"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
                
                <div>
                  <h1 className="text-3xl font-heading font-bold mb-1">{client.full_name}</h1>
                  <p className="text-muted-foreground">{client.email}</p>
                  {client.company_name && (
                    <p className="text-sm text-muted-foreground">{client.company_name}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleManageClientPayment}>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Manage Payment
                </Button>
                <Button variant="outline" onClick={() => setShowTagDialog(true)}>
                  <TagIcon className="w-4 h-4 mr-2" />
                  Add Tag
                </Button>
                <Button onClick={() => setShowEditDialog(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Client
                </Button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{formatPrice(client.total_revenue || 0)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Services</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{client.active_services_count || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{client.total_orders || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">Member Since</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-medium">{formatDate(client.created_at)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="services">Services ({client.services?.length || 0})</TabsTrigger>
              <TabsTrigger value="orders">Orders ({client.orders?.length || 0})</TabsTrigger>
              <TabsTrigger value="notes">Notes ({client.notes?.length || 0})</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Contact Information */}
                <Card>
                  <CardHeader>
                    <CardTitle>Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <a href={`mailto:${client.email}`} className="text-primary hover:underline">
                          {client.email}
                        </a>
                      </div>
                    </div>
                    {client.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Phone</p>
                          <a href={`tel:${client.phone}`} className="text-primary hover:underline">
                            {client.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    {client.company_name && (
                      <div className="flex items-center gap-3">
                        <Building className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Company</p>
                          <p className="font-medium">{client.company_name}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Joined</p>
                        <p className="font-medium">{formatDate(client.created_at)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Billing Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Receipt className="w-5 h-5" />
                      Billing Information
                    </CardTitle>
                    <CardDescription>Used for receipts and accounting</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {client.vat_number && (
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">VAT Number</p>
                          <p className="font-medium">{client.vat_number}</p>
                        </div>
                      </div>
                    )}
                    {(client.billing_address || client.billing_city) && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Billing Address</p>
                          <p className="font-medium">
                            {client.billing_address && <span>{client.billing_address}<br /></span>}
                            {client.billing_city && <span>{client.billing_city} </span>}
                            {client.billing_postal_code && <span>{client.billing_postal_code}<br /></span>}
                            {client.billing_country && <span>{client.billing_country}</span>}
                          </p>
                        </div>
                      </div>
                    )}
                    {!client.vat_number && !client.billing_address && (
                      <p className="text-sm text-muted-foreground">No billing information provided</p>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setShowEditDialog(true)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Billing Info
                    </Button>
                  </CardContent>
                </Card>

                {/* Tags & Groups */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle>Tags & Groups</CardTitle>
                    <CardDescription>Categorize and organize this client</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-sm font-medium mb-2">Tags</p>
                      <div className="flex flex-wrap gap-2">
                        {client.tags && client.tags.length > 0 ? (
                          client.tags.map((tag, idx) => (
                            <Badge key={idx} variant="secondary">
                              {tag}
                              <button
                                onClick={() => handleRemoveTag(tag)}
                                className="ml-2 hover:text-destructive"
                              >
                                ×
                              </button>
                            </Badge>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">No tags</p>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setShowTagDialog(true)}>
                          + Add Tag
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Groups</p>
                      <div className="flex flex-wrap gap-2">
                        {client.groups && client.groups.length > 0 ? (
                          client.groups.map((group, idx) => (
                            <Badge key={idx} variant="outline">
                              {group}
                            </Badge>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">No groups</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services">
              <Card>
                <CardHeader>
                  <CardTitle>Active & Past Services</CardTitle>
                </CardHeader>
                <CardContent>
                  {client.services && client.services.length > 0 ? (
                    <div className="space-y-4">
                      {client.services.map((service) => (
                        <div key={service.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex items-center gap-4">
                            <Package className="w-8 h-8 text-primary" />
                            <div>
                              <h4 className="font-semibold">{service.service_name}</h4>
                              <p className="text-sm text-muted-foreground">{service.pricing_tier_name}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-sm text-muted-foreground">Renewal</p>
                              <p className="font-medium">{formatDate(service.end_date)}</p>
                            </div>
                            <Badge variant={service.status === 'active' ? 'default' : 'secondary'}>
                              {service.status}
                            </Badge>
                            <div className="text-right">
                              <p className="font-semibold">{formatPrice(service.price_paid)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No services yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <Card>
                <CardHeader>
                  <CardTitle>Order History</CardTitle>
                </CardHeader>
                <CardContent>
                  {client.orders && client.orders.length > 0 ? (
                    <div className="space-y-4">
                      {client.orders.map((order) => (
                        <div key={order.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex items-center gap-4">
                            <ShoppingBag className="w-8 h-8 text-primary" />
                            <div>
                              <h4 className="font-semibold">Order #{order.id.slice(0, 8)}</h4>
                              <p className="text-sm text-muted-foreground">{formatDate(order.created_at)}</p>
                              <p className="text-sm text-muted-foreground">{order.items?.length || 0} items</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <Badge variant={order.status === 'completed' ? 'default' : 'secondary'}>
                              {order.status}
                            </Badge>
                            <div className="text-right">
                              <p className="font-semibold text-lg">{formatPrice(order.total)}</p>
                              {order.payment_session_id && (
                                <a
                                  href={`https://dashboard.stripe.com/test/payments/${order.payment_session_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary hover:underline flex items-center gap-1"
                                >
                                  View in Stripe <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-muted-foreground py-8">No orders yet</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes">
              <Card>
                <CardHeader>
                  <CardTitle>Internal Notes</CardTitle>
                  <CardDescription>Notes are only visible to team members</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Add Note */}
                  <div className="space-y-2">
                    <Label>Add New Note</Label>
                    <Textarea
                      placeholder="Write a note about this client..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={3}
                    />
                    <Button onClick={handleAddNote} disabled={!newNote.trim()}>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Add Note
                    </Button>
                  </div>

                  {/* Notes List */}
                  <div className="space-y-3 pt-4 border-t">
                    {client.notes && client.notes.length > 0 ? (
                      client.notes.map((note) => (
                        <div key={note.id} className="p-4 bg-muted rounded-lg">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium">{note.author_name}</p>
                              <p className="text-xs text-muted-foreground">{formatDate(note.created_at)}</p>
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteNote(note.id)}
                              className="text-destructive"
                            >
                              Delete
                            </Button>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{note.note}</p>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No notes yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Edit Dialog */}
          <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Client</DialogTitle>
                <DialogDescription>Update client information and billing details</DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                {/* Basic Info */}
                <div>
                  <h4 className="font-medium mb-3">Contact Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name</Label>
                      <Input
                        value={editForm.full_name || ''}
                        onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Company</Label>
                      <Input
                        value={editForm.company_name || ''}
                        onChange={(e) => setEditForm({...editForm, company_name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editForm.phone || ''}
                        onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      />
                    </div>
                  </div>
                </div>

                {/* Billing Info */}
                <div>
                  <h4 className="font-medium mb-3">Billing Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>VAT Number</Label>
                      <Input
                        value={editForm.vat_number || ''}
                        onChange={(e) => setEditForm({...editForm, vat_number: e.target.value})}
                        placeholder="e.g., EL123456789"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Billing Address</Label>
                      <Input
                        value={editForm.billing_address || ''}
                        onChange={(e) => setEditForm({...editForm, billing_address: e.target.value})}
                        placeholder="Street address"
                      />
                    </div>
                    <div>
                      <Label>City</Label>
                      <Input
                        value={editForm.billing_city || ''}
                        onChange={(e) => setEditForm({...editForm, billing_city: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Postal Code</Label>
                      <Input
                        value={editForm.billing_postal_code || ''}
                        onChange={(e) => setEditForm({...editForm, billing_postal_code: e.target.value})}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Country</Label>
                      <Input
                        value={editForm.billing_country || ''}
                        onChange={(e) => setEditForm({...editForm, billing_country: e.target.value})}
                        placeholder="e.g., Greece"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="flex justify-between sm:justify-between">
                <Button 
                  variant="destructive" 
                  onClick={() => {
                    setShowEditDialog(false);
                    setShowDeleteDialog(true);
                  }}
                  disabled={!canDeleteClient()}
                  data-testid="delete-client-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Client
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                  <Button onClick={handleUpdateClient}>Save Changes</Button>
                </div>
              </DialogFooter>
              {!canDeleteClient() && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Cannot delete client with active services. Cancel or transfer services first.
                </p>
              )}
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-5 h-5" />
                  Delete Client
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete <strong>{client?.full_name}</strong>? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">
                  Deleting this client will:
                </p>
                <ul className="text-sm text-destructive list-disc list-inside mt-2 space-y-1">
                  <li>Deactivate their account</li>
                  <li>Remove them from the active clients list</li>
                  <li>Preserve their order history for records</li>
                </ul>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteClient}
                  disabled={deleting}
                  data-testid="confirm-delete-client-btn"
                >
                  {deleting ? 'Deleting...' : 'Yes, Delete Client'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Tag Dialog */}
          <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Tag</DialogTitle>
                <DialogDescription>Add a tag to categorize this client</DialogDescription>
              </DialogHeader>
              <div>
                <Label>Tag Name</Label>
                <Input
                  placeholder="e.g., VIP, Enterprise, High Value"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowTagDialog(false)}>Cancel</Button>
                <Button onClick={handleAddTag}>Add Tag</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </Layout>
    </TooltipProvider>
  );
}
